// Same value as the SDK's SeverityLevel.Error; the SDK is lazy-loaded so it never loads when telemetry is off.
const SEVERITY_ERROR = 3;
const MAX_MESSAGE_LENGTH = 2000;
const SENSITIVE_QUERY_PATTERN = /(token|access_token|id_token|refresh_token|code|password|secret|key|apikey|api_key|signature|sig|email)/i;

function sanitizeMessage(message) {
  return message
    .replace(/Bearer\s+[^\s,;]+/gi, 'Bearer [REDACTED]')
    .slice(0, MAX_MESSAGE_LENGTH);
}

function sanitizeUrl(value) {
  if (!value || typeof value !== 'string') return value;

  try {
    const url = new URL(value, window.location.origin);
    if (!url.search && !url.hash) return value;

    const shouldRedactAll = Array.from(url.searchParams.keys()).some(key => SENSITIVE_QUERY_PATTERN.test(key));
    if (shouldRedactAll) {
      url.search = '?[REDACTED]';
    } else {
      url.search = '';
    }
    url.hash = '';

    return value.startsWith('http') ? url.toString() : `${url.pathname}${url.search}`;
  } catch {
    return value.replace(/\?[^\s#]*/g, '');
  }
}

function sanitizeTelemetryItem(envelope) {
  const baseData = envelope?.baseData;
  if (!baseData) return true;

  if (envelope.baseType === 'RemoteDependencyData') {
    baseData.name = sanitizeUrl(baseData.name);
    baseData.data = sanitizeUrl(baseData.data);
    baseData.target = sanitizeUrl(baseData.target);
  }

  if (baseData.message) {
    baseData.message = sanitizeMessage(baseData.message);
  }

  if (baseData.properties) {
    Object.keys(baseData.properties).forEach(key => {
      const value = baseData.properties[key];
      if (typeof value === 'string') {
        baseData.properties[key] = sanitizeMessage(sanitizeUrl(value));
      }
    });
  }

  return true;
}

function formatConsoleArgument(value) {
  if (value instanceof Error) {
    return `${value.name}: ${value.message}`;
  }
  if (value === null || value === undefined) {
    return String(value);
  }
  if (typeof value === 'object') {
    return `[${value.constructor?.name || 'Object'}]`;
  }
  return String(value);
}

function toError(error) {
  return error instanceof Error ? error : new Error(String(error));
}

function captureConsoleError(state, args) {
  const { client } = state;
  const message = sanitizeMessage(args.map(formatConsoleArgument).join(' '));
  const exception = args.find(argument => argument instanceof Error);

  if (!client) return;

  if (exception) {
    client.trackException({
      exception,
      properties: {
        source: 'console.error',
        consoleMessage: message
      }
    });
    return;
  }

  client.trackTrace({
    message,
    severityLevel: SEVERITY_ERROR,
    properties: { source: 'console.error' }
  });
}

function installConsoleErrorCapture(state) {
  if (state.originalConsoleError) return;

  state.originalConsoleError = console.error;
  console.error = (...args) => {
    state.originalConsoleError.apply(console, args);
    try {
      captureConsoleError(state, args);
    } catch (error) {
      state.originalConsoleError.call(console, 'Application Insights failed to capture console.error:', error);
    }
  };
}

class ApplicationInsightsService {
  static async initialize(state, connectionString) {
    if (state.initialized) return state.client;

    // Missing configuration is an intentional no-op when browser telemetry is disabled for an environment.
    const configuredConnectionString = connectionString?.trim();
    if (!configuredConnectionString) return null;

    state.initialized = true;

    try {
      const { ApplicationInsights } = await import('@microsoft/applicationinsights-web');
      state.client = new ApplicationInsights({
        config: {
          connectionString: configuredConnectionString,
          disableAjaxTracking: false,
          disableFetchTracking: false,
          enableAjaxPerfTracking: true,
          enableRequestHeaderTracking: false,
          enableResponseHeaderTracking: false,
          enableUnhandledPromiseRejectionTracking: true,
          // Chrome blocks 'unload' listeners via Permissions-Policy; SDK falls back to pagehide/visibilitychange.
          disablePageUnloadEvents: ['unload']
        }
      });
      state.client.loadAppInsights();
      state.client.addTelemetryInitializer(sanitizeTelemetryItem);
      state.client.trackPageView();
      installConsoleErrorCapture(state);
      return state.client;
    } catch (error) {
      state.client = null;
      state.initialized = false;
      console.error('Application Insights initialization failed:', error);
      return null;
    }
  }

  static captureVueError(state, error, info) {
    const exception = toError(error);
    const properties = {
      source: 'vue',
      lifecycleHook: info || 'unknown'
    };

    state.client?.trackException({
      exception,
      properties
    });

    const consoleError = state.originalConsoleError || console.error;
    consoleError.call(console, 'Unhandled Vue error:', exception);
  }
}

export default ApplicationInsightsService;